import { useEffect, useState, useCallback } from "react";
import { Modal, Button, Table, message, Popconfirm } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import * as S from "./styles";
import api from "../../service/api";

interface DataItem {
    key: string;
    empresa: string;
    cnpj: string;
    banco: string;
    conta: string;
    contaCorrente?: string;
    dataInicial?: string;
    dataFinal?: string;
    nomeArquivo?: string;
    dataImportacao?: string;
    hash?: string;
}

interface Props {
    visible: boolean;
    onClose: () => void;
}

function parseDateImportacao(str: string | undefined): number {
    if (!str) return 0;
    const parts = str.split("/");
    if (parts.length === 3) {
        // DD/MM/YYYY → YYYY-MM-DD para o Date entender corretamente
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime() || 0;
    }
    return new Date(str).getTime() || 0;
}

export default function ModalDeletarOFX({ visible, onClose }: Props) {
    const [rawData, setRawData] = useState<DataItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState<string | null>(null);
    const [loadingBulkDelete, setLoadingBulkDelete] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [filterEmpresa, setFilterEmpresa] = useState("");
    const [filterBanco, setFilterBanco] = useState("");
    const [filterConta, setFilterConta] = useState("");

    const fetchData = useCallback(async () => {
        const cliente = localStorage.getItem("cliente");
        if (!cliente) return;
        const { nomeDoGrupo } = JSON.parse(cliente);
        setIsLoading(true);
        try {
            const response = await api.get("/Extratos/listarArquivosPorEmpresaOFX", {
                params: { grupName: nomeDoGrupo },
            });
            if (response.data.success) {
                const formatted: DataItem[] = response.data.result.map((item: any) => ({
                    key: item.hash,
                    empresa: item.empresa,
                    cnpj: item.cnpj,
                    banco: item.banco,
                    conta: item.contaCorrente,
                    contaCorrente: item.contaCorrente,
                    dataImportacao: item.data,
                    dataInicial: item.dataInicial,
                    dataFinal: item.dataFinal,
                    nomeArquivo: item.nomeArquivo,
                    hash: item.hash,
                }));
                const sorted = [...formatted].sort((a, b) =>
                    parseDateImportacao(b.dataImportacao) - parseDateImportacao(a.dataImportacao)
                );
                setRawData(sorted);
            } else {
                message.error("Erro ao carregar lista de arquivos: " + response.data.message);
            }
        } catch {
            message.error("Erro ao carregar lista de arquivos para exclusão.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (visible) {
            setSelectedKeys([]);
            setFilterEmpresa("");
            setFilterBanco("");
            setFilterConta("");
            fetchData();
        }
    }, [visible, fetchData]);

    const deletarOfx = async (hash: string) => {
        try {
            await api.request({
                method: "DELETE",
                url: `/Extratos/deletarArquivosOFX?hash=${encodeURIComponent(hash)}`,
            });
        } catch (error: any) {
            if (error.response) {
                message.error("Erro ao deletar: " + (error.response.data.message || "Erro desconhecido."));
            } else if (error.request) {
                message.error("Sem resposta do servidor. Verifique sua conexão.");
            } else {
                message.error("Erro inesperado ao deletar arquivos!");
            }
            throw error;
        }
    };

    const handleDeleteSingle = async (hash: string) => {
        setLoadingDelete(hash);
        try {
            await deletarOfx(hash);
            await fetchData();
            message.success("Extrato deletado com sucesso!");
        } catch {
            message.error("Erro ao excluir o extrato.");
        } finally {
            setLoadingDelete(null);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedKeys.length === 0) return;
        setLoadingBulkDelete(true);
        try {
            await Promise.all(selectedKeys.map((hash) => deletarOfx(hash)));
            message.success(`${selectedKeys.length} extrato(s) deletado(s) com sucesso!`);
            setSelectedKeys([]);
            await fetchData();
        } catch {
            message.error("Erro ao deletar os extratos selecionados.");
        } finally {
            setLoadingBulkDelete(false);
        }
    };

    const empresaOptions = Array.from(new Set(rawData.map(i => i.empresa))).filter(Boolean);
    const bancoOptions = Array.from(
        new Set(
            (filterEmpresa ? rawData.filter(i => i.empresa === filterEmpresa) : rawData).map(i => i.banco)
        )
    ).filter(Boolean);
    const contaOptions = Array.from(
        new Set(
            rawData
                .filter(i => (!filterEmpresa || i.empresa === filterEmpresa) && (!filterBanco || i.banco === filterBanco))
                .map(i => i.contaCorrente)
        )
    ).filter(Boolean);

    const displayData = rawData
        .filter(i =>
            (!filterEmpresa || i.empresa === filterEmpresa) &&
            (!filterBanco || i.banco === filterBanco) &&
            (!filterConta || i.contaCorrente === filterConta)
        )
        .sort((a, b) =>
            parseDateImportacao(b.dataImportacao) - parseDateImportacao(a.dataImportacao)
        );

    return (
        <Modal
            title=""
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1400}
        >
            <S.ModalHeader>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", marginBottom: "8px", marginTop: "24px" }}>
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                        <S.MenuContainer>
                            <select
                                value={filterEmpresa}
                                onChange={(e) => { setFilterEmpresa(e.target.value); setFilterBanco(""); setFilterConta(""); }}
                            >
                                <option value="">Selecionar Empresa</option>
                                {empresaOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        </S.MenuContainer>
                        <S.MenuContainer>
                            <select
                                value={filterBanco}
                                onChange={(e) => { setFilterBanco(e.target.value); setFilterConta(""); }}
                            >
                                <option value="">Selecionar Banco</option>
                                {bancoOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        </S.MenuContainer>
                        <S.MenuContainer>
                            <select value={filterConta} onChange={(e) => setFilterConta(e.target.value)}>
                                <option value="">Selecionar Conta</option>
                                {contaOptions.map((opt, idx) => <option key={idx} value={opt}>{opt}</option>)}
                            </select>
                        </S.MenuContainer>
                    </div>
                    {selectedKeys.length > 0 && (
                        <Popconfirm
                            title={`Deletar ${selectedKeys.length} extrato(s) selecionado(s)?`}
                            icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                            onConfirm={handleBulkDelete}
                            okText="Sim"
                            cancelText="Não"
                        >
                            <Button type="primary" danger loading={loadingBulkDelete} style={{ borderRadius: "4px" }}>
                                Deletar selecionados ({selectedKeys.length})
                            </Button>
                        </Popconfirm>
                    )}
                </div>
            </S.ModalHeader>
            <S.ModalContainer>
                <Table<DataItem>
                    loading={isLoading}
                    pagination={{ pageSize: 10, showSizeChanger: false, showQuickJumper: true }}
                    rowSelection={{
                        selectedRowKeys: selectedKeys,
                        onChange: (keys) => setSelectedKeys(keys as string[]),
                        getCheckboxProps: () => ({ disabled: loadingBulkDelete }),
                    }}
                    bordered
                    dataSource={displayData}
                    columns={[
                        { title: "Empresa", dataIndex: "empresa", key: "empresa" },
                        { title: "CNPJ", dataIndex: "cnpj", key: "cnpj" },
                        { title: "Banco", dataIndex: "banco", key: "banco" },
                        { title: "Conta Corrente", dataIndex: "contaCorrente", key: "contaCorrente" },
                        { title: "Data Importação", dataIndex: "dataImportacao", key: "dataImportacao" },
                        { title: "Data Inicial", dataIndex: "dataInicial", key: "dataInicial" },
                        { title: "Data Final", dataIndex: "dataFinal", key: "dataFinal" },
                        { title: "Nome Arquivo", dataIndex: "nomeArquivo", key: "nomeArquivo" },
                        {
                            key: "actions",
                            width: 50,
                            render: (_, record) => (
                                <Popconfirm
                                    title="Gostaria de deletar esse extrato?"
                                    icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                                    onConfirm={() => record.hash && handleDeleteSingle(record.hash)}
                                    okText="Sim"
                                    cancelText="Não"
                                >
                                    <Button
                                        type="link"
                                        danger
                                        loading={loadingDelete === record.hash}
                                        disabled={loadingBulkDelete}
                                        icon={
                                            <img
                                                src={`${process.env.PUBLIC_URL}/trash.png`}
                                                alt="delete"
                                                style={{
                                                    width: 20,
                                                    height: 20,
                                                    filter: "invert(13%) sepia(100%) saturate(7481%) hue-rotate(357deg) brightness(92%) contrast(106%)",
                                                }}
                                            />
                                        }
                                    />
                                </Popconfirm>
                            ),
                        },
                    ]}
                />
            </S.ModalContainer>
        </Modal>
    );
}
